import {
  Building,
  Channel,
  Contact,
  DatabaseFile,
  IAddress,
  Property,
  PropertyMetadata,
  PropertyType,
} from '../../src/app/shared/models/entity';
import {
  auditFields,
  createMany,
  cycle,
  FixtureBuilder,
  FixtureFactory,
  fixtureId,
} from './fixture.utils';

// -----------------------------------------------------------------------------------------------------
// @ SAMPLE POOLS
// -----------------------------------------------------------------------------------------------------

const ADDRESSES: readonly IAddress[] = [
  { street: 'Lindenstraße', houseNumber: '12', zipCode: '10115', city: 'Berlin' },
  { street: 'Goethestraße', houseNumber: '45a', zipCode: '20095', city: 'Hamburg' },
  { street: 'Bergmannstraße', houseNumber: '3', zipCode: '80331', city: 'München' },
  { street: 'Kastanienallee', houseNumber: '78', zipCode: '50667', city: 'Köln' },
];

const BRANCHES: readonly [string, string][] = [
  [fixtureId('branch', 0), 'Niederlassung Berlin'],
  [fixtureId('branch', 1), 'Niederlassung Hamburg'],
];

// -----------------------------------------------------------------------------------------------------
// @ FACTORY
// -----------------------------------------------------------------------------------------------------

/**
 * Builds a Property with realistic defaults. Every field can be overridden.
 *
 * The totals (`totalShares`, `totalArea`, `unitCount`) default to a plausible standalone value — use
 * `withBuildings()` on the builder to recompute them from the actual unit graph.
 *
 * @param overrides - The fields to override.
 * @param index - The zero-based index, driving the deterministic id and the sample rotation.
 * @returns {Property} A fresh Property instance.
 */
export const createProperty: FixtureFactory<Property> = (
  overrides: Partial<Property> = {},
  index: number = 0,
): Property => {
  const address: IAddress = cycle(ADDRESSES, index);
  const [branchId, branchName] = cycle(BRANCHES, index);

  return new Property({
    id: fixtureId('property', index),
    ...auditFields(index),
    number: 1000 + index,
    name: `${address.street} ${address.houseNumber}`,
    type: 'Weg',
    street: address.street,
    houseNumber: address.houseNumber,
    zipCode: address.zipCode,
    city: address.city,
    branchId,
    branchName,
    totalShares: 1000,
    totalArea: 0,
    unitCount: 0,
    metadata: new PropertyMetadata(),
    buildings: [],
    documents: [],
    channels: [],
    contacts: [],
    ...overrides,
  });
};

/**
 * Builds a list of Properties.
 *
 * @param count - How many properties to build.
 * @param overrides - Static overrides, or a callback returning overrides per index.
 * @returns {Property[]} The built properties.
 */
export function createProperties(
  count: number,
  overrides?: Partial<Property> | ((index: number) => Partial<Property>),
): Property[] {
  return createMany(createProperty, count, overrides);
}

// -----------------------------------------------------------------------------------------------------
// @ BASE FIXTURES
// -----------------------------------------------------------------------------------------------------

export const DEFAULT_PROPERTY: Property = createProperty();

// -----------------------------------------------------------------------------------------------------
// @ SCENARIO FIXTURES
// -----------------------------------------------------------------------------------------------------

/** A WEG — the default management type. */
export const PROPERTY_WEG: Property = createProperty(
  { type: 'Weg', unitCount: 12, totalArea: 890, totalShares: 1000 },
  0,
);

/** A Mietverwaltung — no co-ownership shares. */
export const PROPERTY_RENTAL: Property = createProperty(
  { type: 'Rental', unitCount: 8, totalArea: 620, totalShares: 0 },
  1,
);

/** A Sondereigentumsverwaltung — a single unit. */
export const PROPERTY_SEV: Property = createProperty(
  { type: 'Sev', unitCount: 1, totalArea: 74.2, totalShares: 7.42 },
  2,
);

/** A freshly created property — no buildings, no units, no documents. */
export const PROPERTY_EMPTY: Property = createProperty(
  {
    unitCount: 0,
    totalArea: 0,
    totalShares: 0,
    buildings: [],
    documents: [],
    channels: [],
    contacts: [],
  },
  3,
);

// -----------------------------------------------------------------------------------------------------
// @ BUILDER
// -----------------------------------------------------------------------------------------------------

export class PropertyBuilder extends FixtureBuilder<Property> {
  // -----------------------------------------------------------------------------------------------------
  // @ CONSTRUCTOR
  // -----------------------------------------------------------------------------------------------------

  constructor(index: number = 0) {
    super(createProperty({}, index));
  }

  // -----------------------------------------------------------------------------------------------------
  // @ PUBLIC METHODS
  // -----------------------------------------------------------------------------------------------------

  /**
   * Sets the management type of the property.
   *
   * @param type - The management type.
   * @returns {this} The builder, for chaining.
   */
  ofType(type: PropertyType): this {
    return this.with({ type });
  }

  /**
   * Sets the postal address and keeps the display name in sync with it.
   *
   * @param address - The address fields to apply.
   * @returns {this} The builder, for chaining.
   */
  withAddress(address: IAddress): this {
    return this.with({
      ...(address as Partial<Property>),
      name: `${address.street} ${address.houseNumber}`,
    });
  }

  /**
   * Attaches the buildings and recomputes `unitCount`, `totalArea` and `totalShares` from their units.
   *
   * @param buildings - The buildings of the property.
   * @returns {this} The builder, for chaining.
   */
  withBuildings(buildings: Building[]): this {
    const units = buildings.flatMap((building: Building) => building.units);

    return this.with({
      buildings,
      unitCount: units.length,
      totalArea: round(units.reduce((total: number, unit) => total + unit.area, 0)),
      totalShares: round(units.reduce((total: number, unit) => total + unit.share, 0)),
    });
  }

  /**
   * Attaches the owners and other contacts of the property.
   *
   * @param contacts - The contacts to attach.
   * @returns {this} The builder, for chaining.
   */
  withContacts(contacts: Contact[]): this {
    return this.with({ contacts });
  }

  /**
   * Attaches the documents of the property, and optionally the thumbnail.
   *
   * @param documents - The documents to attach.
   * @param thumbnail - The thumbnail file. Defaults to the first document.
   * @returns {this} The builder, for chaining.
   */
  withDocuments(documents: DatabaseFile[], thumbnail?: DatabaseFile): this {
    return this.with({ documents, thumbnail: thumbnail ?? documents[0] });
  }

  /**
   * Attaches the communication channels of the property.
   *
   * @param channels - The channels to attach.
   * @returns {this} The builder, for chaining.
   */
  withChannels(channels: Channel[]): this {
    return this.with({ channels });
  }

  /**
   * Assigns the property to a branch.
   *
   * @param branchId - The id of the branch.
   * @param branchName - The denormalized name of the branch.
   * @returns {this} The builder, for chaining.
   */
  inBranch(branchId: string, branchName: string): this {
    return this.with({ branchId, branchName });
  }

  // -----------------------------------------------------------------------------------------------------
  // @ OVERRIDE METHODS
  // -----------------------------------------------------------------------------------------------------

  override build(): Property {
    return new Property(this.draft);
  }
}

// -----------------------------------------------------------------------------------------------------
// @ PRIVATE
// -----------------------------------------------------------------------------------------------------

/**
 * Rounds a total to two decimals, so the seeded sums don't carry float noise.
 *
 * @param value - The value to round.
 * @returns {number} The rounded value.
 */
function round(value: number): number {
  return Math.round(value * 100) / 100;
}
